import { useEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import API_BASE from "../../config";
import { ChargeInfoButton, ChargePaymentsModal } from "./ChargePaymentsModal";
import s from "./SOA.module.css";
import { currentSchool } from "../../utils/currentSchool";
import { extractErrorMessage } from "../../utils/apiError";

function getUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

function fmt(n) {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function pct(n) {
  if (n === null || n === undefined || n === "") return "0%";
  return `${Number(n)}%`;
}

function dateShort(d) {
  if (!d) return "";
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) return String(d);
  return parsed.toLocaleDateString();
}

function div(a, b) {
  const n = Number(a), d = Number(b);
  if (!d || Number.isNaN(n) || Number.isNaN(d)) return 0;
  return Math.round((n / d) * 100) / 100;
}

const DISCOUNT_LABELS = {
  PIONEER: "PIONEER",
  HONORS: "With Honors",
  HIGH_HONORS: "With High Honors",
  SIBLING: "Sibling",
  REFERRAL: "Referral",
  FULL_PAYMENT: "Full Payment",
  INVESTOR: "Investor",
  OTHERS: "Others",
};
const DISCOUNT_ORDER = ["PIONEER", "HONORS", "HIGH_HONORS", "SIBLING", "REFERRAL", "FULL_PAYMENT", "INVESTOR", "OTHERS"];

export default function StatementOfAccountPage() {
  const user = getUser();
  const role = user?.role;
  const isAdmin = role === "Admin" || role === "SuperAdmin";
  // Cashier / CashierStaff get the same "pick any student, view SoA"
  // surface as admins, but stay gated out of the footer edit controls.
  const canBrowseAll = isAdmin || role === "Cashier" || role === "CashierStaff";

  const [students, setStudents]         = useState([]);
  const [schoolYears, setSchoolYears]   = useState([]);
  const [studentId, setStudentId]       = useState("");
  const [schoolYearId, setSchoolYearId] = useState("");
  const [studentSearch, setStudentSearch] = useState("");

  // Extra lookups for the card-grouped student picker.
  const [sections,    setSections]    = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [groupBy,     setGroupBy]     = useState("student"); // "student" | "section" | "grade"

  const [soa, setSoa]         = useState(null);
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);

  // Guardians for the printed SOA — fetched per-student so the sheet shows
  // every linked parent / guardian (Name · Relationship · Contact) instead
  // of the old fixed Mother/Father/Guardian slots. One row per link.
  const [guardians, setGuardians] = useState([]);

  const sheetRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  // Per-charge payment-history modal target — opens from the (i) icon
  // next to each Additional Charges row. The icon is rendered only for
  // admin/cashier viewers because the backend GET endpoint that powers
  // the modal (GET /api/additional-charges/{id}/payments) is gated to
  // those roles via AdminGuard.requireAdmin. Showing it to a parent or
  // student would just produce a 403 on click.
  const [chargeHistoryTarget, setChargeHistoryTarget] = useState(null);

  /** Print path — uses the SAME html2canvas → jsPDF pipeline as
   *  Download PDF so the printed output is pixel-identical to the
   *  PDF, which in turn is pixel-identical to what's on screen.
   *
   *  Why not native @media print:
   *  The SOA sheet's natural width (~1080px desktop) doesn't fit
   *  A4 portrait (~750px usable). CSS `transform: scale()` can
   *  visually shrink it, but the browser's print-pagination
   *  reads the un-transformed bounding box, so we end up either
   *  truncating right-hand columns (what was happening) or
   *  competing with the browser's "fit to page" auto-scale.
   *  Going through a canvas snapshot side-steps all of that —
   *  the PDF is a flat image, and the browser's print stack
   *  reliably scales an image to fit a page.
   *
   *  Flow: same canvas + multi-page pagination as Download PDF →
   *  embed PDF in a hidden iframe → call iframe.contentWindow.print().
   *  pdf.autoPrint() adds a /OpenAction directive as a belt-and-braces
   *  for PDF viewers that don't honour print() from script. */
  async function handlePrint() {
    if (!sheetRef.current) return;
    setPdfBusy(true);
    setError("");
    try {
      const canvas = await html2canvas(sheetRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        // Same filter as Download PDF — keeps the printed sheet free of
        // admin warnings + per-row (i) info buttons.
        ignoreElements: (el) =>
          el.classList?.contains(s.soaWarning) ||
          el.classList?.contains(s.noPdf),
      });

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      const imgData = canvas.toDataURL("image/png");

      // Single page fast path / multi-page pagination — duplicates
      // the Download PDF logic so the two outputs paginate identically.
      if (imgH <= pageH - margin * 2) {
        pdf.addImage(imgData, "PNG", margin, margin, imgW, imgH);
      } else {
        let heightLeft = imgH;
        let position = margin;
        pdf.addImage(imgData, "PNG", margin, position, imgW, imgH);
        heightLeft -= (pageH - margin * 2);
        while (heightLeft > 0) {
          position = margin - (imgH - heightLeft);
          pdf.addPage();
          pdf.addImage(imgData, "PNG", margin, position, imgW, imgH);
          heightLeft -= (pageH - margin * 2);
        }
      }

      // /OpenAction → auto-trigger print when the PDF loads. Combined
      // with the iframe.print() call below, this covers PDF viewers
      // that honour either mechanism.
      pdf.autoPrint();

      // Tear down any prior print iframe so a repeat-click doesn't
      // stack hidden iframes in the DOM.
      const existing = document.getElementById("soa-print-frame");
      if (existing) existing.remove();

      const blobUrl = pdf.output("bloburl");
      const frame = document.createElement("iframe");
      frame.id = "soa-print-frame";
      // Off-screen, zero-size — invisible to the user, but still
      // navigable / printable. Some browsers refuse to print a
      // display:none iframe, so we use width/height 0 + left:-9999px.
      frame.style.cssText = "position:fixed;left:-9999px;top:0;width:1px;height:1px;border:0;opacity:0;";
      const trigger = () => {
        try { frame.contentWindow?.focus(); frame.contentWindow?.print(); }
        catch (e) { /* fall back to PDF /OpenAction */ }
      };
      frame.onload = trigger;
      frame.src = blobUrl;
      document.body.appendChild(frame);
      // Failsafe if onload never fires (some browsers swallow it for
      // blob: URLs). 1.2s is enough for the PDF to be parsed.
      setTimeout(trigger, 1200);
      // Generous cleanup — must outlast the print dialog or the user
      // sees a blank preview if they take a while to click Print.
      setTimeout(() => {
        try { URL.revokeObjectURL(blobUrl); } catch { /* already revoked */ }
        frame.remove();
      }, 60000);
    } catch (e) {
      setError(`Print failed: ${e.message}`);
    } finally {
      setPdfBusy(false);
    }
  }
  // Safety net: tear down any leftover print iframe on unmount so it
  // doesn't accumulate in the DOM across navigations.
  useEffect(() => () => {
    const f = document.getElementById("soa-print-frame");
    if (f) f.remove();
  }, []);

  async function handleDownloadPDF() {
    if (!sheetRef.current) return;
    setPdfBusy(true);
    try {
      const canvas = await html2canvas(sheetRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        // Skip any admin-only warning banner so it doesn't appear in
        // the PDF, and skip on-screen-only affordances (the (i) icon
        // next to each Additional Charges row) so the printed sheet
        // stays clean.
        ignoreElements: (el) =>
          el.classList?.contains(s.soaWarning) ||
          el.classList?.contains(s.noPdf),
      });

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();   // 210
      const pageH = pdf.internal.pageSize.getHeight();  // 297
      const margin = 10;
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      const imgData = canvas.toDataURL("image/png");

      // Single-page fast path
      if (imgH <= pageH - margin * 2) {
        pdf.addImage(imgData, "PNG", margin, margin, imgW, imgH);
      } else {
        // Multi-page: draw the same image offset upward so the next page
        // shows the next slice. Clipping is done by the page bounds.
        let heightLeft = imgH;
        let position = margin;
        pdf.addImage(imgData, "PNG", margin, position, imgW, imgH);
        heightLeft -= (pageH - margin * 2);
        while (heightLeft > 0) {
          position = margin - (imgH - heightLeft);
          pdf.addPage();
          pdf.addImage(imgData, "PNG", margin, position, imgW, imgH);
          heightLeft -= (pageH - margin * 2);
        }
      }

      const safe = (str) => (str || "").replace(/[\\/:*?"<>|]+/g, "").trim();
      const filename = `SOA - ${safe(soa?.student?.name)} - ${safe(soa?.schoolYear?.label)}.pdf`;
      pdf.save(filename);
    } catch (e) {
      setError(`PDF failed: ${e.message}`);
    } finally {
      setPdfBusy(false);
    }
  }

  // Parent's linked children — fetched once, then filtered by SY in the next effect.
  const [parentChildren, setParentChildren] = useState([]);

  // Footer values (officer name/title + note) — read-only here, edited by
  // the SuperAdmin in Settings → Statement of Account. Stored in the
  // site_content key/value table.
  const [footer, setFooter] = useState({ officerName: "", officerTitle: "", note: "" });

  async function loadFooter() {
    try {
      const res = await fetch(`${API_BASE}/api/site-content`);
      if (!res.ok) return;
      const map = await res.json();
      setFooter({
        officerName:  map["soa.officer.name"]  || "",
        officerTitle: map["soa.officer.title"] || "",
        note:         map["soa.footer.note"]   || "",
      });
    } catch { /* leave defaults */ }
  }

  useEffect(() => {
    loadFooter();
    // Card grouping needs these lookups; non-fatal if any fail.
    Promise.all([
      fetch(`${API_BASE}/api/sections`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/grade-levels`).then(r => r.ok ? r.json() : []),
    ]).then(([sec, gl]) => {
      setSections(sec || []);
      setGradeLevels(gl || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const sy = await fetch(`${API_BASE}/api/school-years`).then(r => r.json());
        setSchoolYears(sy);
        const active = sy.find(y => y.active) || sy[0];
        if (active) setSchoolYearId(String(active.id));

        if (role === "Student") {
          const id = user?.linkedEntity?.id;
          if (id) setStudentId(String(id));
        } else if (role === "Parent") {
          const parentId = user?.linkedEntity?.id;
          if (parentId) {
            const links = await fetch(`${API_BASE}/api/parent-student?parentId=${parentId}`).then(r => r.json());
            const kids = await Promise.all(
              links.map(l => fetch(`${API_BASE}/api/students/${l.studentId}`).then(r => r.json()))
            );
            setParentChildren(kids);
          }
        } else {
          // Admin / Cashier / etc — honor ?studentId=X so the Admissions
          // "View Statement of Account" shortcut lands already pointed at
          // the newly-enrolled student. Also honor ?schoolYearId=Y so
          // the "View full SOA" button on the Tuition Payment History
          // modal lands on the SAME year the user was already viewing
          // (otherwise we'd default back to the active SY and the
          // numbers wouldn't match what they had open).
          try {
            const qp = new URLSearchParams(window.location.search);
            const qsId = qp.get("studentId");
            if (qsId) setStudentId(String(qsId));
            const qsSy = qp.get("schoolYearId");
            if (qsSy) setSchoolYearId(String(qsSy));
          } catch { /* ignore unparseable URLs */ }
        }
      } catch (e) { setError(e.message); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch the student list whenever the school year changes. Admins get only
  // students enrolled in that SY; parents get only their children in that SY.
  useEffect(() => {
    if (!schoolYearId) { setStudents([]); return; }
    if (role === "Student") return;

    let cancelled = false;
    (async () => {
      try {
        if (canBrowseAll) {
          const list = await fetch(`${API_BASE}/api/students?schoolYearId=${schoolYearId}`).then(r => r.json());
          if (!cancelled) setStudents(list);
        } else if (role === "Parent") {
          // Reuse the /api endpoint per child to filter by SY is overkill — simpler
          // to fetch the per-SY list and intersect with the parent's linked children.
          const list = await fetch(`${API_BASE}/api/students?schoolYearId=${schoolYearId}`).then(r => r.json());
          const myKidIds = new Set(parentChildren.map(k => k.id));
          if (!cancelled) setStudents(list.filter(st => myKidIds.has(st.id)));
        }
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolYearId, parentChildren]);

  // If the currently-picked student isn't in the filtered list anymore, clear
  // it. Runs for the zero-students case too — otherwise switching to a SY
  // that has no enrolled students would leave the previous student's SOA
  // sheet visible below a "No students in this school year yet" picker.
  useEffect(() => {
    if (!studentId) return;
    if (role === "Student") return; // student picker locked to self
    if (!students.some(st => String(st.id) === String(studentId))) {
      setStudentId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students]);

  useEffect(() => {
    if (!studentId || !schoolYearId) { setSoa(null); return; }
    let cancelled = false;
    setLoading(true); setError("");
    (async () => {
      try {
        const q = `studentId=${studentId}&schoolYearId=${schoolYearId}`;
        const res = await fetch(`${API_BASE}/api/tuition/soa?${q}`);
        if (!res.ok) throw new Error(await extractErrorMessage(res, `Failed (${res.status})`));
        const data = await res.json();
        if (!cancelled) setSoa(data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [studentId, schoolYearId]);

  // Fetch this student's guardians (parent links + parent rows) so the
  // SOA's Guardians section can show one entry per linked parent/guardian
  // — Name · Relationship · Contact. Cancellation-safe; primary first.
  useEffect(() => {
    if (!studentId) { setGuardians([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const [linksRes, parentsRes] = await Promise.all([
          fetch(`${API_BASE}/api/parent-student?studentId=${studentId}`),
          fetch(`${API_BASE}/api/parents`),
        ]);
        if (!linksRes.ok || !parentsRes.ok) throw new Error("link/parent fetch failed");
        const links     = await linksRes.json();
        const allParents = await parentsRes.json();
        const linksArr   = Array.isArray(links) ? links : [];
        const parentsArr = Array.isArray(allParents) ? allParents : [];
        const byId = new Map(parentsArr.map(p => [p.id, p]));
        const rows = linksArr
          .map(l => {
            const p = byId.get(l.parentId);
            if (!p) return null;
            const name = [p.lastName, p.firstName].filter(Boolean).join(", ")
                      || [p.firstName, p.lastName].filter(Boolean).join(" ")
                      || "(Unnamed)";
            return {
              id:           p.id,
              name,
              relationship: l.relationship || "Guardian",
              contact:      p.contact || "",
              primary:      !!l.primary,
            };
          })
          .filter(Boolean)
          // Primary first, then alphabetical by name — same ordering used
          // in the StudentDetailModal so the printed sheet stays consistent.
          .sort((a, b) => {
            if (a.primary !== b.primary) return a.primary ? -1 : 1;
            return a.name.localeCompare(b.name);
          });
        if (!cancelled) setGuardians(rows);
      } catch {
        if (!cancelled) setGuardians([]);
      }
    })();
    return () => { cancelled = true; };
  }, [studentId]);

  // Narrow the Student dropdown by name / student number. Match is
  // case-insensitive and searches last/first name + student #. The
  // currently-selected student stays in the list even if they don't match
  // the search, otherwise the <select> would silently show the wrong name.
  const visibleStudents = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    let list = !q ? students : students.filter(st => {
      const hay = [st.lastName, st.firstName, st.middleName, st.studentNumber]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
    if (studentId && !list.some(st => String(st.id) === studentId)) {
      const picked = students.find(st => String(st.id) === studentId);
      if (picked) list = [picked, ...list];
    }
    return list;
  }, [students, studentSearch, studentId]);

  // Group the (search-filtered) student list for the card picker.
  // Each group: {key, label, students}. `label === null` hides the header
  // (used by the flat "Students" mode so there's no "All" banner).
  const studentGroups = useMemo(() => {
    const sectionById = Object.fromEntries((sections || []).map(x => [x.id, x]));
    const gradeById   = Object.fromEntries((gradeLevels || []).map(g => [g.id, g]));
    const base        = visibleStudents;

    if (groupBy === "student") {
      return [{ key: "all", label: null, students: base }];
    }

    if (groupBy === "section") {
      const map = new Map();
      for (const st of base) {
        const sec = st.currentSectionId ? sectionById[st.currentSectionId] : null;
        const grade = sec ? gradeById[sec.gradeLevelId] : null;
        const key = sec ? `sec-${sec.id}` : "sec-none";
        const label = sec
          ? (grade ? `${grade.name} · ${sec.name}` : sec.name)
          : "No section assigned";
        const ordering = grade?.ordering ?? 9999;
        if (!map.has(key)) map.set(key, { key, label, students: [], ordering });
        map.get(key).students.push(st);
      }
      return [...map.values()].sort((a, b) => a.ordering - b.ordering || a.label.localeCompare(b.label));
    }

    if (groupBy === "grade") {
      const map = new Map();
      for (const st of base) {
        const sec = st.currentSectionId ? sectionById[st.currentSectionId] : null;
        const grade = sec ? gradeById[sec.gradeLevelId] : null;
        const key = grade ? `gl-${grade.id}` : "gl-none";
        const label = grade ? grade.name : "No grade level";
        const ordering = grade?.ordering ?? 9999;
        if (!map.has(key)) map.set(key, { key, label, students: [], ordering });
        map.get(key).students.push(st);
      }
      return [...map.values()].sort((a, b) => a.ordering - b.ordering);
    }

    return [];
  }, [visibleStudents, groupBy, sections, gradeLevels]);

  function studentInitials(st) {
    const a = st?.firstName?.[0] || "";
    const b = st?.lastName?.[0]  || "";
    return (a + b).toUpperCase() || "?";
  }

  const discountRows = useMemo(() => {
    const byType = {};
    (soa?.discounts || []).forEach(d => { byType[d.type] = d; });
    return DISCOUNT_ORDER.map(t => ({
      type: t,
      label: DISCOUNT_LABELS[t],
      row: byType[t] || null,
    }));
  }, [soa]);

  const showStudentPicker = canBrowseAll || role === "Parent";

  return (
    <div className={s.page}>
      <div className={s.controls}>
        <label className={s.filterField}>
          <span>School Year</span>
          <select value={schoolYearId} onChange={e => setSchoolYearId(e.target.value)}>
            <option value="">— select —</option>
            {schoolYears.map(y => (
              <option key={y.id} value={y.id}>
                {y.label}{y.active ? " · Active" : ""}
              </option>
            ))}
          </select>
        </label>

        {showStudentPicker && (
          <label className={`${s.filterField} ${s.searchField}`}>
            <span>
              Search Student
              {studentSearch && (
                <span className={s.matchHint}>
                  {" "}· {visibleStudents.length} match{visibleStudents.length === 1 ? "" : "es"}
                </span>
              )}
            </span>
            <div className={s.searchWrap}>
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
                   className={s.searchIcon} aria-hidden>
                <circle cx="9" cy="9" r="6" />
                <path d="M15 15l-3.5-3.5" />
              </svg>
              <input
                value={studentSearch}
                onChange={e => setStudentSearch(e.target.value)}
                placeholder="Name or student #…"
              />
              {studentSearch && (
                <button type="button" className={s.clearBtn}
                        onClick={() => setStudentSearch("")} aria-label="Clear">✕</button>
              )}
            </div>
          </label>
        )}

        {soa && !soa.error && (
          <>
            {/* Native Print — flips body[data-printing="soa"] so the
                print stylesheet scopes the page to just the SOA sheet.
                Sits next to Download PDF so the operator picks the
                output channel that fits their workflow (print = paper,
                PDF = email / archive). */}
            <button
              type="button"
              className={s.printBtn || s.pdfBtn}
              onClick={handlePrint}
              disabled={pdfBusy}
              title="Print the statement of account">
              🖨 Print
            </button>
            <button
              type="button"
              className={s.pdfBtn}
              onClick={handleDownloadPDF}
              disabled={pdfBusy}
              title={soa.warning
                ? "Export blank statement — fees are zero because no fee structure exists for this term yet"
                : "Download the statement as a PDF file"}>
              {pdfBusy ? "Generating…" : "Download PDF"}
            </button>
          </>
        )}
      </div>

      {/* ── Student card picker ──────────────────────────────────────── */}
      {showStudentPicker && schoolYearId && (
        <div className={s.pickerWrap}>
          <div className={s.groupByRow}>
            <span className={s.segLabel}>Group by</span>
            <div className={s.segmented} role="tablist" aria-label="Group students by">
              {[
                ["student", "Students"],
                ["section", "Sections"],
                ["grade",   "Grade Level"],
              ].map(([val, lbl]) => (
                <button
                  key={val}
                  type="button"
                  role="tab"
                  aria-selected={groupBy === val}
                  className={`${s.segBtn} ${groupBy === val ? s.segBtnActive : ""}`}
                  onClick={() => setGroupBy(val)}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          <div className={s.pickerScroll}>
          {studentGroups.length === 0 || studentGroups.every(g => g.students.length === 0) ? (
            <div className={s.empty}>
              {studentSearch
                ? "No students match your search."
                : "No students in this school year yet."}
            </div>
          ) : (
            studentGroups.map(group => (
              <div key={group.key} className={s.group}>
                {group.label && (
                  <div className={s.groupHead}>
                    {group.label}
                    <span className={s.groupCount}>
                      {group.students.length} student{group.students.length === 1 ? "" : "s"}
                    </span>
                  </div>
                )}
                <div className={s.cardGrid}>
                  {group.students.map(st => {
                    const isActive = String(st.id) === studentId;
                    return (
                      <button
                        key={`${group.key}-${st.id}`}
                        type="button"
                        className={`${s.studentCard} ${isActive ? s.studentCardActive : ""}`}
                        onClick={() => setStudentId(String(st.id))}>
                        <div className={s.studentAvatar}>{studentInitials(st)}</div>
                        <div className={s.studentCardBody}>
                          <div className={s.studentName}>
                            {st.lastName}, {st.firstName}
                          </div>
                          <div className={s.studentMeta}>{st.studentNumber || "—"}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
          </div>
        </div>
      )}

      {error && <div className={s.error}>{error}</div>}
      {/* Only show "Loading" and the sheet once a student is actually picked.
          Before that, the card picker above is self-explanatory — no need
          for a redundant "Pick a student…" empty state below. */}
      {studentId && loading && <div className={s.empty}>Loading…</div>}
      {studentId && !loading && !error && soa?.error && (
        <div className={s.empty}>{soa.error}</div>
      )}

      {studentId && soa && !soa.error && (
        <div className={s.sheet} ref={sheetRef}>
          {soa.warning && (
            // Soft warning — sheet still renders so admins can print/export
            // a blank form. Hidden from the PDF (see ignoreElements above).
            <div className={s.soaWarning}>⚠ {soa.warning}</div>
          )}
          <div className={s.sheetTitle}>
            {/* Multi-tenant letterhead — reads from session so a
                chain admin context-switched to NCLA Cebu generates
                an SOA with NCLA Cebu's name + address, not the chain
                default. Falls back to the bootstrap NCLA Main values
                for legacy sessions predating Phase 2A. */}
            {(() => {
              const sc = currentSchool();
              return (
                <>
                  <div className={s.schoolName}>{sc.name || "Northpoint Center for Learning and Advancement"}</div>
                  <div className={s.schoolAddr}>
                    {sc.address || sc.shortName || ""}
                  </div>
                </>
              );
            })()}
            <div className={s.docTitle}>STATEMENT OF ACCOUNT</div>
          </div>

          <div className={s.headerGrid}>
            {/* Left + middle columns flow naturally; Guardians cell on the
                right is pinned to col 4, spanning all 4 rows. */}
            <Cell label="Issued to:" value={<strong className={s.studentName}>{soa.student?.name || "—"}</strong>} span={2} />
            <Cell label="Year Level:" value={soa.gradeLevel?.name || "—"} />

            <Cell label="Address:" value="" span={2} />
            <Cell label="Academic Year:" value={soa.schoolYear?.label || "—"} />

            <Cell label="LRN:" value="" span={2} />
            <Cell label="Date of Birth:" value="" />

            <Cell label="" value="" span={2} />
            <Cell label="" value="" />

            {/* Guardians — one entry per linked parent / guardian.
                Supports one or many: each row shows Name + Relationship
                (with a primary tag) and contact when available.
                Stays inside the same gold-outlined cell so the SOA's
                visual grid is preserved on print + PDF export. */}
            <Cell
              label="Guardians:"
              gridColumn="4"
              gridRow="1 / span 4"
              className={s.guardiansCell}
              value={
                guardians.length === 0 ? (
                  <span className={s.guardianEmpty}>—</span>
                ) : (
                  <ul className={s.guardianList}>
                    {guardians.map(g => (
                      <li key={g.id} className={s.guardianRow}>
                        <div className={s.guardianTop}>
                          <span className={s.guardianName}>{g.name}</span>
                          {g.primary && <span className={s.guardianPrimary}>Primary</span>}
                        </div>
                        <div className={s.guardianMeta}>
                          <span className={s.guardianRel}>{g.relationship}</span>
                          {g.contact && (
                            <>
                              <span className={s.guardianDot}>·</span>
                              <span className={s.guardianContact}>{g.contact}</span>
                            </>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )
              }
            />
          </div>

          <div className={s.feeBlock}>
            <div className={s.feeCol}>
              <KV label="Tuition Fee :" value={fmt(soa.tuitionFee)} />
              {discountRows.map(({ type, label, row }) => (
                <KV key={type}
                    label={
                      <span className={s.discount}>
                        {label}
                        {row && <AppliesTo row={row} />}
                      </span>
                    }
                    value={row ? pct(row.percentage) : ""}
                    extra={row?.notes} />
              ))}
              {/* Hairline divider — separates the per-discount list above
                  (e.g. "Others · 17%") from the per-fee totals below
                  (e.g. "Tuition Discount: 77%"). Pushed left into the
                  feeBlock's 16px padding so it touches the panel's outer
                  border, and width is capped at the kv's (label + value)
                  span (1.6fr + 1fr of 1.6fr+1fr+1.5fr ≈ 63.4%) so it
                  ends right where the percentage column does — never
                  bleeds into the "extra" notes column to the right. */}
              <div style={{
                height: 1,
                background: "#e5dfd4",
                marginLeft: -16,
                marginTop: 6,
                marginBottom: 4,
                width: "calc(63.4% + 16px)",
              }} />
              {/* Per-fee breakdown — only the discounts that flagged that fee.
                  Falls back to the old totalDiscountPercent label when the
                  backend didn't send the per-fee pcts (older build). */}
              {soa.tuitionDiscountPercent != null ? (
                <>
                  <KV label="Tuition Discount:" value={pct(soa.tuitionDiscountPercent)} strong />
                  {Number(soa.miscDiscountPercent)  > 0 && <KV label="Misc Discount:"  value={pct(soa.miscDiscountPercent)}  strong />}
                  {Number(soa.booksDiscountPercent) > 0 && <KV label="Books Discount:" value={pct(soa.booksDiscountPercent)} strong />}
                </>
              ) : (
                <KV label="Total Discount:" value={pct(soa.totalDiscountPercent)} strong />
              )}
              <KV label="Total Tuition Fee :" value={fmt(soa.discountedTuition)}
                  extra={Number(soa.tuitionDiscountPercent) > 0
                    ? `was ${fmt(soa.tuitionFee)} · ${pct(soa.tuitionDiscountPercent)} off`
                    : null} />
              <KV label="Miscellaneous Fee :" value={fmt(soa.discountedMisc ?? soa.miscFee)}
                  extra={Number(soa.miscDiscountPercent) > 0
                    ? `was ${fmt(soa.miscFee)} · ${pct(soa.miscDiscountPercent)} off`
                    : null} />
              <KV label="Books :" value={fmt(soa.discountedBooks ?? soa.booksFee)}
                  extra={Number(soa.booksDiscountPercent) > 0
                    ? `was ${fmt(soa.booksFee)} · ${pct(soa.booksDiscountPercent)} off`
                    : null} />
            </div>
            <div className={s.feeCol}>
              <KV label="Monthly Tuition Fee:" value={fmt(div(soa.discountedTuition, soa.months))} />
              <KV label="Monthly Misc Fee:"    value={fmt(div(soa.discountedMisc  ?? soa.miscFee,  soa.months))} />
              <KV label="Monthly Books Fee:"   value={fmt(div(soa.discountedBooks ?? soa.booksFee, soa.months))} />
            </div>
          </div>

          <div className={s.totalsBand}>
            <div className={s.totBox}>
              <div className={s.totLabel}>TOTAL SCHOOL FEE:</div>
              <div className={s.totValue}>{fmt(soa.totalSchoolFee)}</div>
            </div>
            <div className={s.totBox}>
              <div className={s.totLabel}>REMAINING BALANCE</div>
              <div className={s.totValue}>{fmt(soa.remainingAfterEnrollment)}</div>
              <div className={`${s.totLabel} ${s.spacer}`}>MONTHLY DUE</div>
              <div className={s.totValue}>{fmt(soa.monthlyDue)}</div>
            </div>
            <div className={s.totBox}>
              <div className={s.totLabel}>RUNNING BALANCE (PAYABLE)</div>
              <div className={`${s.totValue} ${Number(soa.runningBalance) > 0 ? s.due : s.clear}`}>
                {fmt(soa.runningBalance)}
              </div>
            </div>
          </div>

          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Month</th>
                  <th>Monthly DUE</th>
                  <th>Particular</th>
                  <th>Amount</th>
                  <th>OR #</th>
                  <th>Date</th>
                  <th>Accumulated Balance</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                <tr className={s.rowGhost}>
                  <td></td><td></td><td></td>
                  <td>{soa.enrollmentRow?.particular || "enrollment fee"}</td>
                  {/* Strictly reflect the PAYMENT, not the template fee.
                      Falling back to `soa.enrollmentFee` here used to make
                      every unpaid student look like they had already paid
                      ₱1,000 — the bug the cashier flagged. */}
                  <td className={s.amt}>{soa.enrollmentRow?.amount != null ? fmt(soa.enrollmentRow.amount) : "-"}</td>
                  <td>
                    <PaymentGroupDot
                      paymentId={soa.enrollmentRow?.paymentId}
                      method={soa.enrollmentRow?.paymentMethod}
                      referenceNo={soa.enrollmentRow?.referenceNo}
                      orNumber={soa.enrollmentRow?.orNumber}
                      paidAt={soa.enrollmentRow?.date} />
                    {soa.enrollmentRow?.orNumber || ""}
                  </td>
                  <td>{dateShort(soa.enrollmentRow?.date)}</td>
                  <td className={`${s.amt} ${Number(soa.enrollmentRow?.accumulatedBalance) <= 0 ? s.paid : ""}`}>
                    {/* Client asked to hide the enrollment fee's accumulated
                        balance on the statement — display only, the value
                        still flows into the months' running totals below. */}
                  </td>
                  {/* Remarks: only describe the "already deducted" credit
                      when an actual enrollment payment exists. The
                      backend now returns null for unpaid rows, so the
                      OR-fallback is gone. */}
                  <td className={s.note}>{soa.enrollmentRow?.remarks || ""}</td>
                </tr>
                {(soa.months_rows || []).map(r => (
                  <tr key={r.index}>
                    <td>{r.index}</td>
                    <td>{r.month}</td>
                    <td className={s.amt}>{fmt(r.monthlyDue)}</td>
                    <td></td>
                    <td className={s.amt}>{r.amount != null ? fmt(r.amount) : "-"}</td>
                    <td>
                      <PaymentGroupDot
                        paymentId={r.paymentId}
                        method={r.paymentMethod}
                        referenceNo={r.referenceNo}
                        orNumber={r.orNumber}
                        paidAt={r.date} />
                      {r.orNumber || ""}
                    </td>
                    <td>{dateShort(r.date)}</td>
                    <td className={`${s.amt} ${Number(r.accumulatedBalance) <= 0 ? s.paid : ""}`}>
                      {fmt(r.accumulatedBalance)}
                    </td>
                    <td></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}></td>
                  <td className={s.totalFoot}>TOTAL</td>
                  <td className={s.amt}>{fmt(soa.totalPaid)}</td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* ── Additional charges (summer class, special curriculum, etc.) ──
              Hidden entirely when the student has none, so existing SOAs
              print exactly as before. The grand-total band below DOES
              render when there are charges, so the parent sees the
              combined "what's owed in total". */}
          {Array.isArray(soa.additionalCharges) && soa.additionalCharges.length > 0 && (
            <>
              <div className={s.additionalChargesHead}>
                Additional Charges
                <span className={s.additionalChargesSub}>
                  Outside the regular tuition — added to the grand total.
                </span>
              </div>
              <div className={s.tableWrap}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th>Particular</th>
                      <th>Amount</th>
                      <th>Total Paid</th>
                      <th>Remaining</th>
                      {/* Recent Paid + OR # describe the SAME transaction
                          (most recent receipt). Placed adjacent so a parent
                          can scan "₱2,257 · OR-12345" as one fact. */}
                      <th>Recent Paid</th>
                      <th>OR #</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {soa.additionalCharges.map((c, i) => {
                      const remaining = Number(c.remaining ?? (Number(c.amount) - Number(c.paidAmount))) || 0;
                      const recent = c.latestPaymentAmount;
                      const hasRecent = recent != null && Number(recent) > 0.005;
                      return (
                        <tr key={c.id ?? i} className={s.addChgRow}
                            style={{ animationDelay: `${Math.min(i * 60, 360)}ms` }}>
                          <td>{i + 1}</td>
                          <td>
                            <span style={{ display: "inline-flex",
                                            alignItems: "center",
                                            flexWrap: "wrap" }}>
                              {c.label}
                              {/* On-screen-only audit affordance for cashiers
                                  and admins. The .noPdf class is matched by
                                  html2canvas's ignoreElements so the printed
                                  sheet still reads like a clean receipt;
                                  parents / students don't see the icon at
                                  all because the backend endpoint it opens
                                  is gated to admin / cashier roles. */}
                              {canBrowseAll && c.id != null && (
                                <span className={s.noPdf}>
                                  <ChargeInfoButton
                                    onClick={() => setChargeHistoryTarget(c)}
                                    label={`View payments for ${c.label}`}
                                  />
                                </span>
                              )}
                            </span>
                          </td>
                          <td className={s.amt}>{fmt(c.amount)}</td>
                          <td className={s.amt}>
                            {Number(c.paidAmount) > 0.005 ? fmt(c.paidAmount) : "-"}
                          </td>
                          <td className={`${s.amt} ${remaining <= 0.005 ? s.paid : ""}`}>
                            {remaining <= 0.005 ? "-" : fmt(remaining)}
                          </td>
                          {/* Recent Paid — the single most recent transaction's
                              amount. Soft gold tint + bold green so the parent
                              can confirm "yes, my last receipt of ₱X is
                              recorded here". The matching OR # column to its
                              right names the receipt that produced it. */}
                          <td className={`${s.amt} ${hasRecent ? s.recentPaidCell : ""}`}>
                            {hasRecent ? fmt(recent) : "-"}
                          </td>
                          <td>{c.orNumber || ""}</td>
                          <td>
                            <span className={`${s.statusPill} ${s[`status${c.status || "UNPAID"}`] || ""}`}>
                              {c.status || "UNPAID"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} className={s.totalFoot}>TOTAL</td>
                      <td className={s.amt}>{fmt(soa.additionalChargesTotal)}</td>
                      <td className={s.amt}>{fmt(soa.additionalChargesPaid)}</td>
                      <td className={`${s.amt} ${Number(soa.additionalChargesRemaining) <= 0 ? s.paid : ""}`}>
                        {fmt(soa.additionalChargesRemaining)}
                      </td>
                      {/* Recent Paid intentionally has no "total" — it's
                          per-transaction by definition; summing the last
                          payment of each charge would be a misleading
                          number, so leave the cell blank under the
                          subtotal rule. Same for OR # / Status. */}
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Grand-total band — only renders alongside charges so SOAs
                  without additionals stay visually identical to before. */}
              <div className={s.grandTotalBand}>
                <div className={s.totBox}>
                  <div className={s.totLabel}>Tuition (incl. enrollment)</div>
                  <div className={s.totValue}>{fmt(soa.totalSchoolFee)}</div>
                </div>
                <div className={s.totBox}>
                  <div className={s.totLabel}>Additional charges</div>
                  <div className={s.totValue}>{fmt(soa.additionalChargesTotal)}</div>
                </div>
                <div className={s.totBox}>
                  <div className={s.totLabel}>GRAND TOTAL</div>
                  <div className={s.totValue}>{fmt(soa.grandTotal)}</div>
                </div>
                <div className={s.totBox}>
                  <div className={s.totLabel}>GRAND BALANCE (PAYABLE)</div>
                  <div className={`${s.totValue} ${Number(soa.grandRunningBalance) > 0 ? s.due : s.clear}`}>
                    {fmt(soa.grandRunningBalance)}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Footer: signatures + officer + note ── */}
          <div className={s.footer}>
            <div className={s.signatureRow}>
              <div className={s.signatureBlock}>
                <div className={s.signatureLine}>&nbsp;</div>
                <div className={s.signatureLabel}>Received by (Parent / Guardian signature over printed name)</div>
              </div>
              <div className={s.signatureBlock}>
                <div className={s.signatureLine}>&nbsp;</div>
                <div className={s.officerName}>{footer.officerName || "—"}</div>
                <div className={s.officerTitle}>{footer.officerTitle || "Accounting Officer"}</div>
              </div>
            </div>

            {footer.note && (
              <div className={s.footerNote}>{footer.note}</div>
            )}
          </div>
        </div>
      )}

      {/* Per-charge payment-history modal — opened from the (i) icon on
          each Additional Charges row. Portal-rendered to <body>, so it
          sits outside .sheet and won't accidentally land in the PDF
          capture (html2canvas only walks sheetRef's subtree). */}
      {chargeHistoryTarget && (
        <ChargePaymentsModal
          charge={chargeHistoryTarget}
          student={soa?.student}
          schoolYearLabel={soa?.schoolYear?.label}
          onClose={() => setChargeHistoryTarget(null)}
        />
      )}
    </div>
  );
}

/* ── Payment-group dot palette ───────────────────────────────────────────
   Stable, deterministic colour per TuitionPayment id so every SOA row
   that came from the SAME physical receipt gets the SAME coloured dot.
   When one ₱5,000 payment cascades through Enrollment + August + Sept,
   all three rows share one colour — the cashier sees the grouping at a
   glance instead of squinting at dates / refs.

   Palette is deliberately conservative: 8 saturated-but-not-neon hues
   that all read clearly on the SOA's blue tinted body, are
   distinguishable from each other, and print acceptably (no gradients).
   Fall-through is mod 8 so a 9th distinct payment cycles back to colour 1
   — an SOA almost never has more than ~12 distinct payments and a
   collision among non-adjacent rows is harmless. */
const PAYMENT_GROUP_COLORS = [
  "#6b3a30", // maroon
  "#1f6a2d", // forest green
  "#1d4fa8", // royal blue
  "#c9a227", // amber
  "#6e42c1", // plum
  "#2d6a6a", // teal
  "#c0392b", // coral
  "#4a5568", // slate
];
function paymentGroupColor(paymentId) {
  if (paymentId == null) return null;
  // Stable hash so the same payment always hashes to the same slot.
  const id = Number(paymentId);
  if (!Number.isFinite(id)) return null;
  return PAYMENT_GROUP_COLORS[Math.abs(id) % PAYMENT_GROUP_COLORS.length];
}

/** Coloured dot that visually groups every SOA row sharing this
 *  paymentId. Hover (or print preview tooltip) shows the payment's
 *  metadata so the cashier can confirm "yes, all rows tagged green
 *  came from receipt OR-2026-0419 dated 5/9/2026 by Cash." */
function PaymentGroupDot({ paymentId, method, referenceNo, orNumber, paidAt }) {
  if (paymentId == null) return null;
  const color = paymentGroupColor(paymentId);
  // `paidAt` here is actually the payment's received DATE (see
  // receivedDate() on the backend), not a timestamp — no hour/minute to
  // show, so format date-only instead of pretending we have a time.
  const dateStr = paidAt
    ? new Date(paidAt).toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric",
      })
    : null;
  // Compose a single-line title — every SOA row with the same colour
  // has the same title text, which is the proof-by-text that they're
  // grouped. Skips fields that are null so cashiers paying cash don't
  // see a phantom "Bank: —" line. OR # is shown first since it's the
  // one field every payment always has; Ref # (bank/wallet txn id) is
  // only present for non-cash methods.
  const titleParts = [`Payment #${paymentId}`];
  if (method)      titleParts.push(method);
  if (orNumber)    titleParts.push(`OR ${orNumber}`);
  if (referenceNo) titleParts.push(`Ref ${referenceNo}`);
  if (dateStr)     titleParts.push(dateStr);
  return (
    <span
      title={titleParts.join(" · ")}
      aria-label={titleParts.join(" · ")}
      style={{
        display: "inline-block",
        width: 9, height: 9,
        borderRadius: "50%",
        background: color,
        // Subtle ring so the dot stands out even on tinted row backgrounds
        // and prints crisply (no shadow blur — print-safe).
        boxShadow: `0 0 0 2px rgba(255,255,255,0.85), 0 0 0 3px ${color}33`,
        marginRight: 6,
        verticalAlign: "middle",
        flexShrink: 0,
      }} />
  );
}

/** Tiny chip row showing which fees a discount applies to. Rendered next to
 *  the discount label so admins can tell at a glance whether a row hits
 *  Tuition, Misc, Books, or any combination. */
function AppliesTo({ row }) {
  const parts = [];
  // Be null-safe: backend default is tuition-only when the flag is absent.
  const tuition = row.appliesTuition ?? true;
  const misc    = row.appliesMisc    ?? false;
  const books   = row.appliesBooks   ?? false;
  if (tuition) parts.push("Tuition");
  if (misc)    parts.push("Misc");
  if (books)   parts.push("Books");
  if (parts.length === 0) return null;
  return (
    <span style={{
      marginLeft: 6,
      fontSize: "0.7rem",
      fontWeight: 700,
      color: "#8a6b14",
      background: "#faf2dd",
      border: "1px solid #e6d7a3",
      borderRadius: 999,
      padding: "1px 8px",
      letterSpacing: "0.3px",
      whiteSpace: "nowrap",
    }}>
      {parts.join(" · ")}
    </span>
  );
}

function Cell({ label, value, span, gridColumn, gridRow, className }) {
  // Inline grid placement so the caller can pin a cell to a specific
  // column / row — used by the Guardians cell which spans 4 rows.
  const style = (span === 2 || gridColumn || gridRow) ? {
    ...(span === 2 ? { gridColumn: "span 2" } : {}),
    ...(gridColumn ? { gridColumn } : {}),
    ...(gridRow    ? { gridRow }    : {}),
  } : undefined;
  return (
    <div className={`${s.cell}${className ? ` ${className}` : ""}`} style={style}>
      <div className={s.cellLabel}>{label}</div>
      <div className={s.cellValue}>{value}</div>
    </div>
  );
}

function KV({ label, value, extra, strong }) {
  return (
    <div className={`${s.kv} ${strong ? s.kvStrong : ""}`}>
      <div className={s.kvLabel}>{label}</div>
      <div className={s.kvValue}>{value}</div>
      {extra && <div className={s.kvExtra}>{extra}</div>}
    </div>
  );
}
